/**
 * The look of each map, after the GDD's previews and mockups (section 10): the sky, its clouds and distant
 * mountains (drawn by Backdrop behind the terrain), and the light on the terrain (colours, tint). Display only.
 */
import { MATERIALS, Physique } from '@invaders/sim';

export interface Mountains {
  /** Colour, and the row of the ridge's mean height. */
  readonly color: string;
  readonly ridge: number;
  /** Height of the peaks around it, and how many across the map. */
  readonly height: number;
  readonly peaks: number;
  /** Horizontal parallax: 0 = fixed on screen, 1 = moves with the map. */
  readonly parallax: number;
  readonly seed: number;
}

export interface Theme {
  /** Vertical gradient of the sky over the map's height: [position 0-1, css colour]. */
  readonly sky: readonly [number, string][];
  readonly clouds?: { readonly color: string; readonly light: string; readonly count: number; readonly top: number; readonly bottom: number };
  /** Far to near. */
  readonly mountains: readonly Mountains[];
  readonly sun?: { readonly x: number; readonly y: number; readonly r: number; readonly color: string; readonly glow: string };
  /** The Earth, seen from the Moon. */
  readonly earth?: { readonly x: number; readonly y: number; readonly r: number };
  readonly stars?: number;
  /** Terrain: colours that differ from the materials' own, then a tint over all of them (r, g, b factors). */
  readonly colors?: Readonly<Record<number, number>>;
  readonly tint?: readonly [number, number, number];
}

// The rock is a purple grey, as in the GDD's previews (sim's material colour); it darkens with depth (ViewRenderer).
const { ROCK, SAND, GRAVEL, LAVA, ICE, WATER } = Physique;

const DAY: Theme = {
  sky: [[0, '#3d5c9e'], [0.45, '#8da8cf'], [0.75, '#a9bddc']],
  clouds: { color: '#a9bcdc', light: '#c3d2ea', count: 9, top: 30, bottom: 170 },
  mountains: [
    { color: '#7c8fae', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 1 },
    { color: '#6d7fa0', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 2 },
  ],
  sun: { x: 820, y: 118, r: 24, color: '#fff4d7', glow: 'rgba(255, 244, 215, 0.18)' },
};

export const THEMES: Readonly<Record<string, Theme>> = {
  island: DAY,
  valley: DAY,
  fort: DAY,
  forest: {
    // Dry: a hazy, warm sky.
    sky: [[0, '#4a6a9a'], [0.5, '#c8b48a'], [0.78, '#e2c896']],
    clouds: { color: '#d4bf98', light: '#e8d6b0', count: 6, top: 40, bottom: 150 },
    mountains: [
      { color: '#b49a78', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 3 },
      { color: '#9c8466', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 4 },
    ],
    sun: { x: 300, y: 150, r: 30, color: '#ffe2a0', glow: 'rgba(255, 210, 120, 0.2)' },
    colors: { [ROCK]: 0x5a4c58 },
  },
  refinery: {
    // Smog over the refinery.
    sky: [[0, '#3a3a44'], [0.5, '#76685e'], [0.78, '#9a8470']],
    clouds: { color: '#5a524e', light: '#6e6660', count: 12, top: 20, bottom: 200 },
    mountains: [
      { color: '#5e5654', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 5 },
      { color: '#4e4846', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 6 },
    ],
    colors: { [ROCK]: 0x4c4450 },
  },
  glacier: {
    // Polar: a pale sky, snowy mountains.
    sky: [[0, '#5a86c0'], [0.5, '#bcd6ea'], [0.78, '#dceaf4']],
    clouds: { color: '#d6e4f0', light: '#eef5fa', count: 7, top: 30, bottom: 160 },
    mountains: [
      { color: '#c4d4e4', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 7 },
      { color: '#a8bcd2', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 8 },
    ],
    sun: { x: 180, y: 100, r: 18, color: '#ffffff', glow: 'rgba(255, 255, 255, 0.22)' },
    colors: { [ROCK]: 0x585468 },
  },
  ember: {
    // Île de Braise: a sky red with the volcano's glow, a dim and reddish light on everything.
    sky: [[0, '#140810'], [0.42, '#3b120f'], [0.58, '#8a2a18'], [0.72, '#3b120f']],
    clouds: { color: '#3a1012', light: '#4a1616', count: 8, top: 30, bottom: 140 },
    mountains: [
      { color: '#4a1410', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 9 },
      { color: '#36100e', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 10 },
    ],
    colors: { [ROCK]: 0x3e3448, [LAVA]: 0xe0501c },
    tint: [1, 0.86, 0.84],
  },
  moon: {
    // The Moon: black sky, stars, the Earth; a grey, lighter moon rock under a lighter regolith.
    sky: [[0, '#040309'], [1, '#0a0912']],
    mountains: [
      { color: '#14131d', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 11 },
      { color: '#1c1b27', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 12 },
    ],
    earth: { x: 850, y: 92, r: 36 },
    stars: 420,
    colors: { [ROCK]: 0x7a7882, [GRAVEL]: 0xa19fa7, [SAND]: 0xb0aaa0 },
  },
  bridge: {
    // Pont de bois: the end of the day over the sea, a low sun, so that the fire shows.
    sky: [[0, '#2c3a70'], [0.42, '#a8628a'], [0.62, '#f09a5c'], [0.78, '#ffd28a']],
    clouds: { color: '#b0647c', light: '#e89a80', count: 8, top: 40, bottom: 220 },
    mountains: [
      { color: '#7a5a84', ridge: 330, height: 34, peaks: 2, parallax: 0.3, seed: 15 },
      { color: '#5e4670', ridge: 362, height: 18, peaks: 4, parallax: 0.5, seed: 16 },
    ],
    sun: { x: 640, y: 262, r: 34, color: '#fff0c0', glow: 'rgba(255, 200, 130, 0.24)' },
    colors: { [ROCK]: 0x54465c },
  },
  dome: {
    // Dôme de vapeur: a cold, clear sky over the ice, warm lava in the rock.
    sky: [[0, '#28456e'], [0.5, '#6e9cb8'], [0.78, '#b4d4dc']],
    clouds: { color: '#a8c4d4', light: '#d4e6ee', count: 6, top: 30, bottom: 140 },
    mountains: [
      { color: '#6484a0', ridge: 318, height: 40, peaks: 2, parallax: 0.3, seed: 17 },
      { color: '#4e6c88', ridge: 352, height: 22, peaks: 4, parallax: 0.5, seed: 18 },
    ],
    sun: { x: 760, y: 84, r: 20, color: '#fdfbf0', glow: 'rgba(255, 250, 225, 0.18)' },
    colors: { [ROCK]: 0x4a4a62, [ICE]: 0xbcecf8 },
  },
  pirate: {
    // Bateau pirate: the tropics, a blue sky and a warm horizon.
    sky: [[0, '#2f7ec8'], [0.5, '#8ccbe6'], [0.78, '#f4e4b4']],
    clouds: { color: '#e6eef2', light: '#ffffff', count: 9, top: 30, bottom: 200 },
    mountains: [
      { color: '#8fb6c8', ridge: 372, height: 16, peaks: 3, parallax: 0.3, seed: 19 },
    ],
    sun: { x: 180, y: 90, r: 26, color: '#fff6d4', glow: 'rgba(255, 244, 200, 0.2)' },
    colors: { [ROCK]: 0x6a5c54, [WATER]: 0x2a8ab8 },
  },
  reactor: {
    // Centrale nucléaire: a sick, greenish dusk; the concrete is grey.
    sky: [[0, '#1c2420'], [0.5, '#46584a'], [0.78, '#94a878']],
    clouds: { color: '#525e50', light: '#68766a', count: 10, top: 20, bottom: 170 },
    mountains: [
      { color: '#3e4c42', ridge: 330, height: 34, peaks: 2, parallax: 0.3, seed: 20 },
      { color: '#303c34', ridge: 362, height: 18, peaks: 4, parallax: 0.5, seed: 21 },
    ],
    colors: { [ROCK]: 0x6c7076 },
  },
  palace: {
    // Palais des glaces: a polar night, stars and a pale moon over icy peaks; the ice bright and bluish.
    sky: [[0, '#070b1e'], [0.45, '#14285a'], [0.72, '#2f5f86']],
    mountains: [
      { color: '#3c5478', ridge: 318, height: 46, peaks: 3, parallax: 0.3, seed: 13 },
      { color: '#2c405f', ridge: 352, height: 24, peaks: 5, parallax: 0.5, seed: 14 },
    ],
    sun: { x: 210, y: 96, r: 16, color: '#e8f2ff', glow: 'rgba(200, 225, 255, 0.2)' },
    stars: 260,
    colors: { [ROCK]: 0x46425e, [ICE]: 0xb4ecff, [GRAVEL]: 0x8a8ca0 },
  },
};

export function themeFor(mapId: string): Theme {
  return THEMES[mapId] ?? DAY;
}

/** Material colour under a theme: its own colour or the theme's, times the theme's tint (0xRRGGBB). */
export function themedColor(theme: Theme, mat: number): number {
  const base = theme.colors?.[mat] ?? MATERIALS[mat]?.color ?? 0;
  const t = theme.tint;
  if (!t) return base;
  const ch = (v: number, f: number) => Math.min(255, Math.round(v * f));
  return (ch((base >> 16) & 0xff, t[0]) << 16) | (ch((base >> 8) & 0xff, t[1]) << 8) | ch(base & 0xff, t[2]);
}
